import React from 'react';
import { X, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/auth/useAuth';
import { createCheckoutSession } from '../../services/stripe/api/checkout';

interface PromoPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PromoPopup({ isOpen, onClose }: PromoPopupProps) {
  const [promoCode, setPromoCode] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // If not logged in, redirect to login with subscription as destination
      if (!isAuthenticated) {
        navigate('/login', { 
          state: { 
            from: '/subscription',
            promoCode: promoCode.toUpperCase()
          },
          replace: true 
        });
        return;
      }

      // Validate promo code
      if (promoCode && promoCode.toUpperCase() !== 'TEZZYFRIEND') {
        toast.error('Invalid promo code');
        setIsLoading(false);
        return;
      }

      // Create checkout session with promo code
      const { url } = await createCheckoutSession('price_1QmMspJS08trBicYWy6hGt5h', {
        promoCode: promoCode.toUpperCase()
      });

      // Redirect to checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#2a1114] rounded-xl max-w-lg w-full relative overflow-hidden">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Logo */}
          <img 
            src="https://i.ibb.co/ZKF53B4/TEZHLY-9.png"
            alt="TEZHLY"
            className="h-8 mx-auto mb-6"
          />

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              Your Personal Career Coach is Waiting
            </h2>
            <p className="text-lg mb-4 text-[#d8f477]">
              For the next 12 hours only, unlock a free month with code: TEZZYFRIEND
            </p>
          </div>

          {/* Benefits */}
          <div className="grid gap-4 mb-8">
            {[
              "Personalized career strategies",
              "Resume reviews & guidance",
              "Exclusive job opportunities",
              "Expert career advice"
            ].map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white/5 p-3 rounded-lg">
                <CheckCircle className="h-5 w-5 text-[#d8f477] flex-shrink-0" />
                <p className="text-white text-sm">{benefit}</p>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="TEZZYFRIEND"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-[#d8f477] focus:border-[#d8f477]"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#d8f477] text-black font-bold py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              ) : (
                <>
                  <span>Claim Your Free Month</span>
                  <Clock className="h-5 w-5" />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-gray-400 mt-6 text-sm">
            Questions? Email{' '}
            <a href="mailto:info@tezhly.com" className="text-[#d8f477] hover:underline">
              info@tezhly.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}